import { useState, useEffect, useCallback, forwardRef } from 'react';
import type React from 'react';
import { useNavigate } from 'react-router-dom';
import { getNotificationVariant } from '../../constants/notificationTypes';
import { notificationService } from '../../services/notifications';
import type { NotificationItem } from '../../services/notifications';
import { formatRelativeTime } from '../../utils/dateFormatters';
import { resolveNotificationLink } from '../../utils/notificationLinks';
import { trackEvent } from '../../services/portalTracking';
import RecommendationText from './RecommendationText';
import styles from './NotificationPanel.module.css';

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called after any in-panel mutation that may change the action-item
   * count (markRead, markAllRead). Parent uses this to refetch the bell
   * badge count immediately rather than waiting for the 60s poll.
   */
  onMutate?: () => void;
}

const NotificationPanel = forwardRef<HTMLDivElement, NotificationPanelProps>(({ isOpen, onClose, onMutate }, ref) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  // Messages arrive within the 220-character contract; the full write-up
  // (extra_data.full_message) opens on demand without leaving the panel.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const [loading, setLoading] = useState(false);
  const [feedbackPending, setFeedbackPending] = useState<string | null>(null);
  const [dismissPending, setDismissPending] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    try {
      const response = await notificationService.listNotifications({
        limit: 20,
        include_dismissed: showResolved,
      });
      setNotifications(response.notifications);
    } catch (err) {
      console.error('Failed to load notifications', err);
    } finally {
      setLoading(false);
    }
  }, [isOpen, showResolved]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleClick = async (notification: NotificationItem) => {
    // Audit trail (owner ask 2026-10-07: "I want to see if they're ever
    // clicking them"): one row per bell item opened, with the notification
    // id and type and whether it took the person somewhere.
    const resolvedLink = resolveNotificationLink(notification.link, notification.extra_data);
    trackEvent('action', 'notification_open', {
      notification_id: notification.id,
      notification_type: notification.notification_type,
      was_unread: !notification.is_read,
      outcome: resolvedLink ? 'navigate' : 'none',
    });
    // Mark as read
    if (!notification.is_read) {
      try {
        await notificationService.markRead(notification.id);
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id ? { ...n, is_read: true } : n
          )
        );
        onMutate?.();
      } catch (err) {
        console.error('Failed to mark notification as read', err);
      }
    }

    // Navigate if link available (rewrite stale links from before PR #138)
    if (resolvedLink) {
      navigate(resolvedLink);
      onClose();
    }
  };

  const handleMarkAllRead = async () => {
    trackEvent('action', 'notification_mark_all_read', {
      count: notifications.filter((n) => !n.is_read).length,
      outcome: 'request',
    });
    try {
      await notificationService.markAllRead();
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: true }))
      );
      onMutate?.();
    } catch (err) {
      console.error('Failed to mark all as read', err);
    }
  };

  const handleDismiss = async (
    e: React.MouseEvent,
    notificationId: string,
  ) => {
    e.stopPropagation();
    const target = notifications.find((n) => n.id === notificationId);
    trackEvent('action', 'notification_resolve', {
      notification_id: notificationId,
      notification_type: target?.notification_type ?? null,
      outcome: 'request',
    });
    setDismissPending(notificationId);
    try {
      await notificationService.dismiss(notificationId);
      if (showResolved) {
        // Keep the row visible but flip its dismissed state so the badge updates.
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId
              ? { ...n, is_dismissed: true, is_read: true }
              : n,
          ),
        );
      } else {
        // Default view hides dismissed items — drop it locally for snappy feedback.
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      }
      onMutate?.();
    } catch (err) {
      console.error('Failed to dismiss notification', err);
    } finally {
      setDismissPending(null);
    }
  };

  const handleFeedback = async (
    e: React.MouseEvent,
    notificationId: string,
    feedback: 'thumbs_up' | 'thumbs_down',
  ) => {
    e.stopPropagation();
    setFeedbackPending(notificationId);
    try {
      await notificationService.submitFeedback(notificationId, feedback);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId
            ? { ...n, user_feedback: feedback, is_read: true }
            : n
        )
      );
    } catch (err) {
      console.error('Failed to submit feedback', err);
    } finally {
      setFeedbackPending(null);
    }
  };

  const getTypeStyle = getNotificationVariant;

  const isEmailNotification = (type: string): boolean => {
    return type.startsWith('email_');
  };

  return (
    <div ref={ref} className={`${styles.panel} ${isOpen ? styles.open : ''}`}>
      <div className={styles.header}>
        <span className={styles.title}>Notifications</span>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {notifications.some((n) => !n.is_read) && (
            <button
              onClick={handleMarkAllRead}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '12px',
                color: 'var(--primary)',
                cursor: 'pointer',
                fontWeight: 500,
              }}
              type="button"
            >
              Mark all read
            </button>
          )}
          <button
            className={styles.close}
            onClick={onClose}
            type="button"
            aria-label="Close notifications"
            data-action="notification_panel_close"
            data-outcome="close"
          >
            &times;
          </button>
        </div>
      </div>
      <label className={styles.showResolvedRow}>
        <input
          type="checkbox"
          checked={showResolved}
          onChange={(e) => setShowResolved(e.target.checked)}
        />
        Show resolved
      </label>
      {loading ? (
        <div style={{ padding: '20px', textAlign: 'center', color: 'var(--gray)' }}>
          Loading...
        </div>
      ) : notifications.length === 0 ? (
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--gray)' }}>
          No notifications
        </div>
      ) : (
        notifications.map((notification) => {
          const typeStyle = getTypeStyle(notification.notification_type);
          const showFeedback = isEmailNotification(notification.notification_type);
          const isUnsure = notification.agent_decision === 'unsure';
          const isPending = feedbackPending === notification.id;
          const isDismissPending = dismissPending === notification.id;
          const isDismissed = notification.is_dismissed;
          return (
            <div
              key={notification.id}
              className={`${styles.item} ${!notification.is_read ? styles.unread : ''} ${isDismissed ? styles.dismissed : ''}`}
              onClick={() => handleClick(notification)}
              data-action="notification_open"
              data-target-notification-id={notification.id}
              data-record-value={notification.notification_type}
              data-outcome={notification.link ? 'navigate' : 'none'}
            >
              <div className={styles.itemTop}>
                <div className={`${styles.icon} ${styles[typeStyle]}`}>
                  {typeStyle === 'danger' && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                    </svg>
                  )}
                  {typeStyle === 'warning' && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                    </svg>
                  )}
                  {typeStyle === 'success' && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  )}
                </div>
                <div className={styles.textBlock}>
                  <div className={styles.titleRow}>
                    <strong>{notification.title}</strong>
                    {isUnsure && (
                      <span className={styles.unsureBadge}>Needs confirmation</span>
                    )}
                    {isDismissed && (
                      <span className={styles.dismissedBadge}>Resolved</span>
                    )}
                  </div>
                  {(() => {
                    const full = typeof notification.extra_data?.full_message === 'string'
                      ? notification.extra_data.full_message
                      : null;
                    const open = expandedIds.has(notification.id);
                    return (
                      <>
                        <RecommendationText
                          text={open && full ? full : (notification.message ?? '')}
                          className={styles.text}
                        />
                        {full && (
                          <button
                            type="button"
                            className={styles.moreBtn}
                            data-testid={`notification-more-${notification.id}`}
                            onClick={(e) => toggleExpanded(e, notification.id)}
                          >
                            {open ? 'Less' : 'More'}
                          </button>
                        )}
                      </>
                    );
                  })()}
                  <div className={styles.time}>{formatRelativeTime(notification.created_at)}</div>
                </div>
              </div>

              {!isDismissed && (
                <div className={styles.itemActions}>
                  <button
                    className={styles.resolveBtn}
                    onClick={(e) => handleDismiss(e, notification.id)}
                    disabled={isDismissPending}
                    title="Mark as resolved"
                    type="button"
                    data-action="notification_resolve"
                    data-target-notification-id={notification.id}
                    data-record-value={notification.notification_type}
                    data-outcome="request"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Mark resolved
                  </button>
                </div>
              )}

              {/* Feedback buttons for email notifications */}
              {showFeedback && (
                <div className={styles.feedbackRow}>
                  {notification.user_feedback ? (
                    <span className={styles.feedbackGiven}>
                      {notification.user_feedback === 'thumbs_up' ? (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"/>
                          </svg>
                          Helpful
                        </>
                      ) : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z"/>
                          </svg>
                          Not helpful
                        </>
                      )}
                    </span>
                  ) : (
                    <div className={styles.feedbackButtons}>
                      <span className={styles.feedbackLabel}>
                        {isUnsure ? 'Is this important?' : 'Helpful?'}
                      </span>
                      <button
                        className={`${styles.thumbBtn} ${styles.thumbUp}`}
                        onClick={(e) => handleFeedback(e, notification.id, 'thumbs_up')}
                        disabled={isPending}
                        title="Yes, this is helpful"
                        type="button"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"/>
                        </svg>
                      </button>
                      <button
                        className={`${styles.thumbBtn} ${styles.thumbDown}`}
                        onClick={(e) => handleFeedback(e, notification.id, 'thumbs_down')}
                        disabled={isPending}
                        title="No, not helpful"
                        type="button"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
});

NotificationPanel.displayName = 'NotificationPanel';

export default NotificationPanel;
